import {useGetBasicDataItems} from '@/modules/basic-data/api'
import {mapBasicDataItemsToSelectItems} from '@/modules/basic-data/functions'
import {oralQuantityBasisOptions} from '@/modules/drug-catalog/constants'
import {MedicationProfileFormFieldsOptions} from '@/modules/drug-catalog/medication-profiles/constants'
import {useGetInfiniteSolutionDrugs} from '@/modules/drug-catalog/solutions/api'
import {StabilityRuleCard} from '@/modules/drug-catalog/stability-rules/components'
import {FormSection} from '@cypro/ui/components/form/form-section'
import {EmptyState} from '@cypro/ui/components/ui/empty-state'
import {withForm} from '@cypro/ui/integrations/form'
import {Box, Button, Grid, HStack} from '@chakra-ui/react'
import {SolutionType} from '@cypro/engine/queries'
import {BasicDataResources} from '@cypro/engine/schema'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'
import {LuPlus} from 'react-icons/lu'

export const MedicationProfileFormFields = withForm({
  ...MedicationProfileFormFieldsOptions,
  render: function Render({form, onAddStabilityRule, onEditStabilityRule, onDeleteStabilityRule, onCancel}) {
    const {t} = useTranslate('common')
    const {t: tProfiles} = useTranslate('medication-profiles')

    const reconstitutionRequired = useStore(form.store, state => state.values.reconstitutionRequired)
    const oralSpecificsRequired = useStore(form.store, state => state.values.oralSpecificsRequired)
    const stabilityRules = useStore(form.store, state => state.values.stabilityRules)

    const solutionDrugs = useGetInfiniteSolutionDrugs({filters: {types: [SolutionType.enum.SOLVENT]}})
    const {items: medicineForms} = useGetBasicDataItems({resource: BasicDataResources.medicineForms})

    const medicineFormsSelectItems = mapBasicDataItemsToSelectItems(medicineForms)

    const {oralQuantityBasisSelectItems} = useMemo(() => {
      const oralQuantityBasisSelectItems = oralQuantityBasisOptions.map(item => ({label: t(item), value: item}))

      return {
        oralQuantityBasisSelectItems,
      }
    }, [t])

    function onChangeMedicineForm(formId: string) {
      const medicineForm = medicineForms?.find(form => form.id === formId)
      if (!medicineForm) return

      form.setFieldValue('reconstitutionRequired', Boolean(medicineForm.reconstitutionRequired))
      form.setFieldValue('isDrySubstance', Boolean(medicineForm.isDrySubstance))
      form.setFieldValue('medicineFormName', medicineForm.name)
      form.setFieldValue('name', medicineForm.name)
    }

    const addStabilityRuleButton = (
      <Button size="sm" variant="ghost" colorPalette="cyan" onClick={() => onAddStabilityRule()}>
        <LuPlus />
        {t('add_rule')}
      </Button>
    )

    return (
      <Grid gridTemplateColumns="27% 73%" gap="0" flex="1" height="100%">
        <Box
          backgroundColor="bg.muted"
          overflowY="auto"
          padding="6"
          borderRightWidth="1px"
          borderRightStyle="solid"
          borderRightColor="border"
        >
          <Box spaceY="8">
            <FormSection label={t('medicine_form')} description={tProfiles('medicine_form_description')}>
              <form.AppField
                name="medicineFormId"
                listeners={{
                  onChange({value}) {
                    const [id] = value
                    if (id) onChangeMedicineForm(id)
                  },
                }}
              >
                {field => (
                  <field.Select
                    label={t('medicine_form')}
                    placeholder={t('eg', {value: medicineFormsSelectItems[0]?.label})}
                    items={medicineFormsSelectItems}
                    required
                  />
                )}
              </form.AppField>

              <form.AppField name="name">
                {field => <field.TextField label={t('name')} placeholder={tProfiles('profile_name_placeholder')} />}
              </form.AppField>

              <Box spaceY="2">
                <form.AppField name="isDrySubstance">
                  {field => <field.CheckBox label={t('is_dry_substance')} size="sm" />}
                </form.AppField>
              </Box>
            </FormSection>

            <FormSection>
              <form.AppField name="oralSpecificsRequired">
                {field => (
                  <field.Switch
                    label={t('oral_specifics')}
                    description={tProfiles('oral_specifics_description')}
                    descriptionProps={{color: 'fg.muted'}}
                  />
                )}
              </form.AppField>

              {oralSpecificsRequired && (
                <form.AppField name="oralQuantityBasis">
                  {field => (
                    <field.Select
                      label={t('oral_calculation_basis')}
                      placeholder={t('eg', {value: oralQuantityBasisSelectItems[0]?.label})}
                      items={oralQuantityBasisSelectItems}
                    />
                  )}
                </form.AppField>
              )}
            </FormSection>

            <FormSection>
              <form.AppField name="reconstitutionRequired">
                {field => (
                  <field.Switch
                    label={t('reconstitution_required')}
                    description={tProfiles('reconstitution_logic_description')}
                    descriptionProps={{color: 'fg.muted'}}
                  />
                )}
              </form.AppField>

              {reconstitutionRequired && (
                <form.AppField name="reconstitutionDiluentsIds">
                  {field => (
                    <field.InfiniteCombobox
                      label={t('reconstitution_solvents')}
                      placeholder={t('eg', {value: t('name')})}
                      multiple
                      {...solutionDrugs}
                    />
                  )}
                </form.AppField>
              )}
            </FormSection>
          </Box>
        </Box>

        <Box padding="6" display="flex" flexDir="column" gap="4" overflowY="auto">
          <Box flex="1" overflowY="auto">
            <FormSection
              label={t('stability_rules')}
              description={tProfiles('stability_rules_description')}
              height="100%"
              display="flex"
              flexDir="column"
              contentProps={
                stabilityRules.length ? {overflowY: 'auto'} : {height: '100%', display: 'flex', alignItems: 'center'}
              }
            >
              {Boolean(stabilityRules.length) && (
                <Grid gridTemplateColumns="1fr 1fr" gapX="4" gapY="4">
                  {stabilityRules.map((rule, index) => {
                    return (
                      <StabilityRuleCard
                        key={rule.id ?? index}
                        rule={rule}
                        onEdit={() => onEditStabilityRule(rule, index)}
                        onDelete={() => onDeleteStabilityRule(index)}
                      />
                    )
                  })}
                </Grid>
              )}

              {!stabilityRules.length && (
                <EmptyState title={t('no_stability_rules_added_yet')} p="0" flex="1">
                  {addStabilityRuleButton}
                </EmptyState>
              )}
            </FormSection>
          </Box>

          <Box spaceY="4">
            {Boolean(stabilityRules.length) && addStabilityRuleButton}

            <HStack gapX="3" justifyContent="space-between">
              <form.AppField name="status">
                {field => (
                  <field.Switch
                    label={field.state.value[0] === 'active' ? t('active_form') : t('inactive_form')}
                    checked={field.state.value[0] === 'active'}
                    onCheckedChange={changes => field.setValue([changes.checked ? 'active' : 'inactive'])}
                    colorPalette="green"
                  />
                )}
              </form.AppField>

              <HStack gapX="3">
                <Button variant="outline" onClick={onCancel}>
                  {t('cancel')}
                </Button>
                <form.SubmitButton>{t('save')}</form.SubmitButton>
              </HStack>
            </HStack>
          </Box>
        </Box>
      </Grid>
    )
  },
})
